// src/utils/imageDrag.js
//
// Dragging an image to another place in the manuscript.
//
// An image sits in the text flow, so a drag does not put it at a point on the
// page: it moves it between two blocks, and the reflow then lays the pages out
// with it there. Dropped near the bottom of a page with "Continue onto next
// page" on, it is cut across the page break like any running image — see
// imageBleed.js.
//
// The places it can go are the gaps between the pages' blocks, with two kinds
// of gap left out: the seam inside a paragraph the page split cut in two (an
// image there would split the paragraph for good — see splitBlocks.js), and
// the seam between a running image and the rest of it on the next page.

import { CONT_ATTR, bleedBlocker, blockOf, isWrapped } from './imageBleed';
import { SPLIT_CONT_ATTR } from './splitBlocks';

/** How far the pointer moves before a press on an image becomes a drag. */
export const DRAG_START_PX = 5;

const isSeam = (b) => !!b && (b.hasAttribute(SPLIT_CONT_ATTR) || b.hasAttribute(CONT_ATTR));

/**
 * The gaps a dragged image can be dropped into, in document order. A gap is
 * between `after` and `before`; either is null at the very start or end.
 *
 * The image has been lifted out by then. `from` is the gap it was lifted from:
 * a gap that touches either side of it is `noop`, since dropping there puts
 * everything back where it was. It is null for an image taken out of a line of
 * words, where every drop moves it onto a line of its own.
 *
 * @param {Element[]} blocks every page block in document order, leaving out
 *   the end spacers
 * @param {{after: Element|null, before: Element|null}|null} from
 * @returns {{after: Element|null, before: Element|null, noop: boolean}[]}
 */
export function dropGaps(blocks, from) {
  const touches = (gap) => !!from && (
    (!!from.after && from.after.isConnected && gap.after === from.after)
    || (!!from.before && from.before.isConnected && gap.before === from.before)
  );
  const gaps = [];
  for (let i = 0; i <= blocks.length; i++) {
    const after = blocks[i - 1] || null;
    const before = blocks[i] || null;
    if (isSeam(before)) continue;
    const gap = { after, before, noop: false };
    gap.noop = touches(gap);
    gaps.push(gap);
  }
  return gaps;
}

/**
 * The block to move for the image `span`, its first piece. An image that is
 * all of its block moves with it. One that shares its line with words or
 * another image is taken out of that line and given a block of its own, the
 * one the insert dialog would have given it: a wrapped image stands alone, any
 * other goes in a paragraph aligned the way it was inserted.
 *
 * @param {Element} span a .rv-resizable-image in a page
 * @returns {Element} the block, ready to be put in its new place
 */
export function liftImage(span) {
  if (!bleedBlocker(span)) return blockOf(span) || span;
  span.remove();
  if (isWrapped(span)) return span;
  const p = span.ownerDocument.createElement('p');
  const align = span.querySelector('img')?.getAttribute('data-alignment');
  if (align) p.style.textAlign = align;
  p.appendChild(span);
  return p;
}

/**
 * The spot nearest `y`, or null when there are none.
 *
 * @param {{y: number}[]} spots
 * @param {number} y in the same coordinates as the spots
 */
export function nearestSpot(spots, y) {
  let best = null;
  for (const s of spots) {
    if (!best || Math.abs(s.y - y) < Math.abs(best.y - y)) best = s;
  }
  return best;
}
